import React, { useRef, useState } from 'react';
import {
  Datagrid,
  TextField,
  useRefresh,
  useRecordContext,
  DateField,
} from 'react-admin';

import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ClearIcon from '@material-ui/icons/Clear';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';

import { DialogoConfirmacao } from '../../Components';
import ActionsList from '../../Components/ReactAdmin/ActionsList';
import ActionsField from '../../Components/ReactAdmin/Fields/ActionsField';
import ListPageTabbed from '../../Components/ReactAdmin/List/ListPageTabbed';
import { ProtegerPagina } from '../../layout/VerificaPermissaoPage';
import { api } from '../../services';
import { PRIMARY_COLOR } from '../../utils';
import {
  STATUS_ATIVO,
  STATUS_DELETADO,
  STATUS_INATIVO,
} from '../../utils/constants';
import { PERMISSAO_TELA_COMPUTADORES_AUTORIZADOS_LISTAR } from '../../utils/dadosTelas';
import DialogoCreateEdit from './DialogoCreateEdit';
import Filter from './Filter';

const PermissionsExpand = ({ record }) => {
  if (!record) return null;

  const itemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 220,
  };

  const labelStyle = {
    fontWeight: 600,
    marginBottom: 2,
  };

  const descStyle = { fontSize: 13, color: '#666' };

  return (
    <div style={{ padding: 16, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
      <div style={itemStyle}>
        {record.permissao_gerencial ? (
          <CheckCircleIcon style={{ color: '#2e7d32' }} />
        ) : (
          <ClearIcon style={{ color: '#999' }} />
        )}
        <div>
          <div style={labelStyle}>Módulo Gerencial</div>
          <div style={descStyle}>
            {record.permissao_gerencial ? 'Acesso permitido' : 'Sem acesso'}
          </div>
        </div>
      </div>

      <div style={itemStyle}>
        {record.permissao_fc ? (
          <CheckCircleIcon style={{ color: PRIMARY_COLOR || '#1976d2' }} />
        ) : (
          <ClearIcon style={{ color: '#999' }} />
        )}
        <div>
          <div style={labelStyle}>Fluxo Financeiro (FC)</div>
          <div style={descStyle}>
            {record.permissao_fc ? 'Acesso permitido' : 'Sem acesso'}
          </div>
        </div>
      </div>

      <div style={itemStyle}>
        {record.permissao_ponto_digital ? (
          <CheckCircleIcon style={{ color: '#6a1b9a' }} />
        ) : (
          <ClearIcon style={{ color: '#999' }} />
        )}
        <div>
          <div style={labelStyle}>Ponto Digital</div>
          <div style={descStyle}>
            {record.permissao_ponto_digital ? 'Acesso permitido' : 'Sem acesso'}
          </div>
        </div>
      </div>
    </div>
  );
};

function getDadosChipStatus(status) {
  switch (status) {
    case STATUS_ATIVO:
      return {
        label: 'Autorizado',
        color: '#2e7d32',
        brackgroundColor: '#edf7ed',
      };

    case STATUS_INATIVO:
      return {
        label: 'Pendente',
        color: '#000000',
        brackgroundColor: '#ffffff',
      };
    case STATUS_DELETADO:
      return {
        label: 'Bloqueado',
        color: '#d32f2f',
        brackgroundColor: '#fdecea',
      };
    default:
      return { label: '', color: '', brackgroundColor: '' };
  }
}

const StatusField = (props) => {
  const record = useRecordContext(props);
  const status = record
    ? record.record
      ? record.record.status
      : STATUS_INATIVO
    : STATUS_INATIVO;

  const { brackgroundColor, color, label } = getDadosChipStatus(status);

  return (
    <Chip
      size="small"
      label={label}
      style={{
        backgroundColor: brackgroundColor,
        color,
        fontWeight: 600,
      }}
    />
  );
};

const useStyles = makeStyles((theme) => ({
  dataHeader: {
    flex: 1,
    backgroundColor: PRIMARY_COLOR,
    padding: '10px',
    color: 'white',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    borderSpacing: 0,
    borderCollapse: 'collapse',
  },
  dataCell: {
    flex: 1,
    padding: '10px',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderSpacing: 0,
    borderCollapse: 'collapse',
  },
}));

const tabs = [
  { id: 'ativos', name: 'Ativos', filter: { status: STATUS_ATIVO } },
  { id: 'pendentes', name: 'Pendentes', filter: { status: STATUS_INATIVO } },
  { id: 'bloqueados', name: 'Bloqueados', filter: { status: STATUS_DELETADO } },
];

const List = (props) => {
  const refDialogoConfirmacao = useRef(null);
  const refresh = useRefresh();
  const classes2 = useStyles();
  const [dialogo, setDialogo] = useState({
    open: false,
    id: null,
  });

  function handleEdit({ id }) {
    setDialogo({
      open: true,
      id,
    });
  }

  async function autorizar(id) {
    await api.put(`/pdv-machines/liberar-acesso/${id}`);
    handleClose();
  }

  async function bloquear(id) {
    await api.put(`/pdv-machines/bloquear-acesso/${id}`);
    handleClose();
  }

  async function handleConfirma({ id, codigo }) {
    switch (codigo) {
      case 1:
        return autorizar(id);
      case 3:
        return bloquear(id);
      default:
    }
  }

  const handleClose = () => {
    refresh();
  };

  const toolbarActions = <ActionsList {...props} />;

  function handleAutorizar(id) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'AUTORIZAR COMPUTADOR PARA SER PDV',
        'Tem certeza que deseja autorizar?',
        {
          id,
          codigo: 1,
        },
      );
    }
  }

  function handleBloquear(id) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'BLOQUEAR COMPUTADOR PARA SER PDV',
        'Tem certeza que deseja bloquear?',
        {
          id,
          codigo: 3,
        },
      );
    }
  }

  const renderGrid = ({ listProps, classes, listContext, ...rest }) => (
    <Datagrid {...props} {...rest}>
      <StatusField
        source="status"
        label="Status"
        headerClassName={classes2.dataHeader}
        cellClassName={classes2.dataCell}
        sortable={false}
      />
      <TextField
        source="id"
        label="Id"
        headerClassName={classes2.dataHeader}
        cellClassName={classes2.dataCell}
        sortable={false}
      />
      <TextField
        source="machine_id"
        label="MachineId"
        headerClassName={classes2.dataHeader}
        cellClassName={classes2.dataCell}
        sortable={false}
      />
      <TextField
        source="descricao"
        label="Descição"
        headerClassName={classes2.dataHeader}
        cellClassName={classes2.dataCell}
        sortable={false}
      />
      <DateField
        source="data_solicitacao"
        label="Data da Solicitação"
        showTime
        headerClassName={classes2.dataHeader}
        cellClassName={classes2.dataCell}
        sortable={false}
      />
      <ActionsField
        textAlign="right"
        label="Ações"
        sortable={false}
        headerClassName={classes2.dataHeader}
        cellClassName={classes2.dataCell}
        actions={[
          {
            title: 'Editar',
            icon: <EditOutlinedIcon />,
            show: () => true,
            onClick: (r) => handleEdit(r),
            disabled: (r) => r.status === STATUS_DELETADO,
          },
          {
            title: 'Autorizar',
            icon: <CheckCircleIcon style={{ color: 'green' }} />,
            show: (r) =>
              r.status === STATUS_INATIVO || r.status === STATUS_DELETADO,
            onClick: (r) => handleAutorizar(r.id),
          },
          {
            title: 'Bloquear',
            icon: <BlockIcon style={{ color: 'red' }} />,
            show: (r) =>
              r.status === STATUS_INATIVO || r.status === STATUS_ATIVO,
            onClick: (r) => handleBloquear(r.id),
          },
        ]}
      />
    </Datagrid>
  );

  return (
    <>
      <ListPageTabbed
        title="Computadores Autorizados"
        filters={<Filter />}
        toolbarActions={toolbarActions}
        renderGrid={(props) =>
          renderGrid({ ...props, expand: <PermissionsExpand /> })
        }
        perPage={50}
        tabs={tabs}
        filterDefaultValues={{ status: STATUS_ATIVO }}
        listProps={{
          sort: { field: 'data_solicitacao', order: 'desc' },
          ...props,
        }}
      />
      <DialogoConfirmacao
        ref={refDialogoConfirmacao}
        handleConfirma={handleConfirma}
      />
      <DialogoCreateEdit
        open={dialogo.open}
        id={dialogo.id}
        onClose={() => {
          setDialogo({
            open: false,
            id: null,
          });
          handleClose();
        }}
        isDialogoConfirmacaoActions
      />
    </>
  );
};

function Wrapper(props) {
  return (
    <ProtegerPagina permissao={PERMISSAO_TELA_COMPUTADORES_AUTORIZADOS_LISTAR}>
      <List {...props} />
    </ProtegerPagina>
  );
}

export default Wrapper;
